import React from 'react';
import { Link } from 'react-router-dom';
import Post from './Post';
import { trackEventIfConsented } from './analyticsConsent';
import { trackInternalNavigationIfConsented } from './pageAnalytics';
import { SETUP_CATEGORY_DEFINITIONS, getSetupPageData, parseListTags } from './setupContent';
import './style.css';

const FEATURED_EDITORIAL_GUIDES = [
  {
    title: 'Desk cable management ideas',
    description: 'Start with the cleanest problem-led guide and branch into trays, monitor arms, and cleaner-workspace upgrades.',
    to: '/collections/desk-cable-management-ideas',
    destination: 'collection_page',
    label: 'Collection guide',
  },
  {
    title: 'Monitor arms for clean desk setups',
    description: 'One of the clearest purchase-adjacent pages for reclaiming desk space and improving monitor positioning.',
    to: '/gear/monitor-arms-for-clean-setups',
    destination: 'gear_page',
    label: 'Gear guide',
  },
  {
    title: 'Laptop and monitor setup ideas for small desks',
    description: 'A compact-layout page that bridges inspiration with accessory decisions for constrained workspaces.',
    to: '/collections/small-desk-setup-ideas-with-laptop-and-monitor',
    destination: 'collection_page',
    label: 'Collection guide',
  },
  {
    title: 'Desk shelf ideas for small desks',
    description: 'A practical next-step guide for people trying to add storage and vertical space without enlarging the desk.',
    to: '/collections/desk-shelf-ideas-for-small-desks',
    destination: 'collection_page',
    label: 'Collection guide',
  },
  {
    title: 'Desk setup accessories for cleaner workspaces',
    description: 'A broad commercial-intent bridge page that connects small upgrades into one cleaner overall desk system.',
    to: '/gear/desk-setup-accessories-for-cleaner-workspaces',
    destination: 'gear_page',
    label: 'Gear guide',
  },
  {
    title: 'Dual monitor desk setup ideas',
    description: 'A layout-first guide for readers already moving beyond a single-screen workspace and comparing next setups.',
    to: '/collections/dual-monitor-desk-setup-ideas',
    destination: 'collection_page',
    label: 'Collection guide',
  },
];

const Posts = ({ posts }) => {
  const featuredPosts = posts.slice(0, 3);
  const latestPreviewPosts = posts.slice(3, 7).length > 0 ? posts.slice(3, 7) : posts.slice(0, 4);

  return (
    <section>
      <header className="home-hero">
        <div className="home-hero-copy">
          <p className="home-hero-kicker">Real desk setups · tagged gear · weekly inspiration</p>
          <h1>Desk setup ideas from real workspaces</h1>
          <p className="home-hero-description">
            Explore real setups, jump into search-ready topic hubs, and discover the gear people actually use across
            cable management, monitor arms, desk lighting, compact layouts, and more.
          </p>
          <div className="home-hero-actions">
            <Link
              className="home-hero-link primary"
              to="/desk-setup-ideas"
              onClick={() => {
                trackEventIfConsented('hero_cta_click', {
                  cta_name: 'explore_desk_setup_ideas',
                  page_context: 'home',
                });
                trackInternalNavigationIfConsented({
                  link_target: '/desk-setup-ideas',
                  link_context: 'home_hero_primary',
                  source_page_group: 'home',
                  destination_page_group: 'search_hub',
                });
              }}
            >
              Explore desk setup ideas
            </Link>
            <Link
              className="home-hero-link"
              to="/latest"
              onClick={() => {
                trackEventIfConsented('hero_cta_click', {
                  cta_name: 'browse_latest_setups',
                  page_context: 'home',
                });
                trackInternalNavigationIfConsented({
                  link_target: '/latest',
                  link_context: 'home_hero_secondary',
                  source_page_group: 'home',
                  destination_page_group: 'latest_page',
                });
              }}
            >
              Browse latest setups
            </Link>
          </div>
        </div>
        <div className="home-hero-highlights" aria-label="Popular desk setup themes">
          <div className="home-hero-card">
            <strong>New homepage job</strong>
            <p>Route readers into topic hubs, featured setups, and the newsletter instead of dropping everyone into a raw feed.</p>
          </div>
          <div className="home-hero-card">
            <strong>Best first wedges</strong>
            <p>Commercially relevant setup ideas like cable management, monitor arms, desk shelves, lighting, and small-desk layouts.</p>
          </div>
        </div>
      </header>

      <div className="home-section">
        <div className="section-heading-block">
          <p className="page-kicker">Primary hubs</p>
          <h2>Start with high-intent setup ideas</h2>
        </div>
        <div className="category-grid">
          {SETUP_CATEGORY_DEFINITIONS.map(category => (
            <Link
              key={category.slug}
              to={`/setup-ideas/${category.slug}`}
              className="category-card"
              onClick={() =>
                trackInternalNavigationIfConsented({
                  link_target: `/setup-ideas/${category.slug}`,
                  link_context: 'home_topic_card',
                  source_page_group: 'home',
                  destination_page_group: 'topic_hub',
                })
              }
            >
              <p className="category-label">{category.shortLabel}</p>
              <h3>{category.title}</h3>
              <p>{category.description}</p>
              <span className="category-cta">Open hub →</span>
            </Link>
          ))}
        </div>
      </div>

      <div className="home-section">
        <div className="section-heading-block">
          <p className="page-kicker">Featured guides</p>
          <h2>Jump straight into the strongest intent pages</h2>
          <p>
            These are the authored guides most likely to match practical search intent right now: cleaner desks, better
            layouts, and higher-clarity workspace upgrades.
          </p>
        </div>
        <div className="category-grid">
          {FEATURED_EDITORIAL_GUIDES.map(guide => (
            <Link
              key={guide.to}
              to={guide.to}
              className="category-card"
              onClick={() =>
                trackInternalNavigationIfConsented({
                  link_target: guide.to,
                  link_context: 'home_featured_guide_card',
                  source_page_group: 'home',
                  destination_page_group: guide.destination,
                })
              }
            >
              <p className="category-label">{guide.label}</p>
              <h3>{guide.title}</h3>
              <p>{guide.description}</p>
              <span className="category-cta">Open guide →</span>
            </Link>
          ))}
        </div>
      </div>

      {featuredPosts.length > 0 ? (
        <div className="home-section" id="PostSection">
          <div className="section-heading-block">
            <p className="page-kicker">Featured setups</p>
            <h2>Fresh setups worth opening</h2>
          </div>
          <div className="post-list-grid compact-grid">
            {featuredPosts.map(model => {
              const setup = getSetupPageData(model);
              return (
                <Post
                  key={model.id}
                  id={model.id}
                  userID={model.userid}
                  imgURL={model.imgURL}
                  listTags={parseListTags(model.listtags)}
                  title={setup.title}
                  summary={setup.excerpt}
                  categoryLabel={setup.primaryCategory?.shortLabel}
                  canonicalPath={setup.path}
                  sharePath={setup.path}
                  altText={setup.altText}
                  pageContext="home_feed"
                />
              );
            })}
          </div>
        </div>
      ) : null}

      <div className="content-grid-two home-section">
        <article className="content-card">
          <h2>How 101Setups now routes traffic</h2>
          <p>
            Homepage to topic hub, topic hub to setup page, and setup page to related inspiration. That gives the site a
            much better crawl path than treating the homepage as the only real public archive.
          </p>
        </article>
        <article className="content-card">
          <h2>Newsletter role</h2>
          <p>
            The newsletter stays important, but as a return channel after readers discover useful setup ideas, not as a
            substitute for a proper content architecture.
          </p>
        </article>
      </div>

      {latestPreviewPosts.length > 0 ? (
        <div className="home-section">
          <div className="section-heading-block section-heading-inline">
            <div>
              <p className="page-kicker">Latest preview</p>
              <h2>The newest setups</h2>
            </div>
            <Link
              className="inline-text-link"
              to="/latest"
              onClick={() =>
                trackInternalNavigationIfConsented({
                  link_target: '/latest',
                  link_context: 'home_latest_preview_link',
                  source_page_group: 'home',
                  destination_page_group: 'latest_page',
                })
              }
            >
              View full latest page →
            </Link>
          </div>
          <div className="post-list-grid compact-grid">
            {latestPreviewPosts.map(model => {
              const setup = getSetupPageData(model);
              return (
                <Post
                  key={model.id}
                  id={model.id}
                  userID={model.userid}
                  imgURL={model.imgURL}
                  listTags={parseListTags(model.listtags)}
                  title={setup.title}
                  summary={setup.excerpt}
                  categoryLabel={setup.primaryCategory?.shortLabel}
                  canonicalPath={setup.path}
                  sharePath={setup.path}
                  altText={setup.altText}
                  pageContext="home_feed"
                />
              );
            })}
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default Posts;
